/**
 * Google Analytics 4 (wave 7b): the tenant's connection to a GA4 property and
 * the Traffic page's numbers. `/api/v1/integrations/ga4` needs
 * `integrations.manage`; `/api/v1/analytics/traffic` needs `analytics.read`
 * and its `.csv` `exports.create`.
 *
 * The service account's key never reaches the browser: only its email does, so
 * a tenant knows whom to add as Viewer on their property.
 */

import { ApiError, type ApiErrorBody } from '@/lib/api'

import type {
  Ga4GroupBy,
  Ga4Integration,
  Ga4ImportRun,
  Ga4ImportStatus,
  Ga4Property,
  Ga4PropertyStatus,
  Ga4StreamCheck,
  Ga4TrafficMetrics,
  Ga4TrafficQuery,
  Ga4TrafficReport,
  Ga4TrafficRow,
} from './types'

export interface Ga4Api {
  ga4Integration(): Promise<Ga4Integration>
  connectGa4(input: { propertyId: string; websiteId?: string }): Promise<Ga4Property>
  /** The traffic already imported is kept. */
  disconnectGa4(ga4PropertyId: string): Promise<Ga4Property>
  /** One property. */
  runGa4Import(ga4PropertyId: string): Promise<Ga4Property[]>
  /** Every connected property, each on its own cooldown: what ran, and what was skipped. */
  runGa4Import(): Promise<Ga4ImportRun>
  /** Read-only, before connecting: a property's web-stream hosts against this tenant's websites. */
  checkGa4Streams(propertyId: string): Promise<Ga4StreamCheck>
  traffic(query: Ga4TrafficQuery): Promise<Ga4TrafficReport>
  /** The same breakdown as a CSV file (`exports.create`); a refusal rejects with the API's error. */
  trafficCsv(query: Ga4TrafficQuery): Promise<Blob>
}

/**
 * A connect refusal that names the hosts it saw (`ownership_not_proven`, when the
 * server sent them — see `docs/architecture/integrations-ga4.md`). Withheld by the
 * server, and so absent here, when naming them would confirm another tenant's site.
 */
export class Ga4OwnershipError extends ApiError {
  constructor(
    status: number,
    code: string,
    message: string,
    correlationId: string | undefined,
    readonly streamHosts: string[],
    readonly websiteHosts: string[],
  ) {
    super(status, code, message, correlationId)
    this.name = 'Ga4OwnershipError'
  }
}

function stringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : null
}

/**
 * The `ApiError` for a connect refusal, enriched with `details.stream_hosts` /
 * `details.website_hosts` when the server sent them. The shared `ApiError` drops
 * `details` entirely, so this reads the response body itself rather than
 * delegating to the shared `request()`/`send()` helpers.
 */
export async function ga4ConnectErrorFrom(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined
  try {
    body = (await response.json()) as ApiErrorBody
  } catch {
    body = undefined
  }
  const message = body?.error?.message ?? response.statusText
  const code = body?.error?.code ?? 'temporary_failure'
  const streamHosts = stringArray(body?.error?.details?.stream_hosts)
  const websiteHosts = stringArray(body?.error?.details?.website_hosts)
  if (streamHosts && websiteHosts) {
    return new Ga4OwnershipError(response.status, code, message, body?.correlation_id, streamHosts, websiteHosts)
  }
  return new ApiError(response.status, code, message, body?.correlation_id)
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface Ga4PropertyOut {
  ga4_property_id: string
  property_id: string
  display_name: string
  website_id: string | null
  timezone: string
  currency: string
  status: Ga4PropertyStatus
  connected_at: string
  last_import_at: string | null
  last_import_status: Ga4ImportStatus
  last_error: string | null
  rows_last_30_days: number
  stale: boolean
  provider_lag: boolean
  next_manual_import_at: string | null
}

export interface Ga4IntegrationOut {
  configured: boolean
  service_account_email: string | null
  configuration_error: string | null
  properties: Ga4PropertyOut[]
}

export interface Ga4ImportSkipOut {
  ga4_property_id: string
  retry_after_seconds: number
}

export interface Ga4ImportRunOut {
  integration: Ga4IntegrationOut
  ran: number
  skipped: Ga4ImportSkipOut[]
}

export interface Ga4StreamCheckOut {
  property_id: string
  stream_hosts: string[]
  website_hosts: string[]
  matches: string[]
  ownership_provable: boolean
}

interface TrafficMetricsOut {
  sessions: number
  users: number
  new_users: number
  engaged_sessions: number
  conversions: number
  purchases: number
  revenue_minor: number | null
}

type PreviousOut = {
  [K in keyof TrafficMetricsOut as `${K}_previous`]?: TrafficMetricsOut[K] | null
}

export interface TrafficRowOut extends TrafficMetricsOut, PreviousOut {
  key: string
  label: string
}

export interface TrafficOut {
  source: 'ga4'
  start: string
  end: string
  group_by: Ga4GroupBy
  compare: 'previous_period' | null
  previous_start: string | null
  previous_end: string | null
  currency: string | null
  properties: Array<{ ga4_property_id: string; property_id: string; display_name: string; currency: string }>
  rows: TrafficRowOut[]
  totals: TrafficMetricsOut
  totals_previous: TrafficMetricsOut | null
  freshness: { last_import_at: string | null; stale: boolean }
}

export function ga4PropertyFromWire(row: Ga4PropertyOut): Ga4Property {
  return {
    ga4PropertyId: row.ga4_property_id,
    propertyId: row.property_id,
    displayName: row.display_name,
    websiteId: row.website_id,
    timezone: row.timezone,
    currency: row.currency,
    status: row.status,
    connectedAt: row.connected_at,
    lastImportAt: row.last_import_at,
    lastImportStatus: row.last_import_status,
    lastError: row.last_error,
    stale: row.stale,
    providerLag: row.provider_lag,
    nextManualImportAt: row.next_manual_import_at,
    rowsLast30Days: row.rows_last_30_days,
  }
}

export function ga4IntegrationFromWire(body: Ga4IntegrationOut): Ga4Integration {
  return {
    configured: body.configured,
    serviceAccountEmail: body.service_account_email,
    configurationError: body.configuration_error,
    properties: body.properties.map(ga4PropertyFromWire),
  }
}

export function ga4ImportRunFromWire(body: Ga4ImportRunOut): Ga4ImportRun {
  return {
    integration: ga4IntegrationFromWire(body.integration),
    ran: body.ran,
    skipped: body.skipped.map((skip) => ({
      ga4PropertyId: skip.ga4_property_id,
      retryAfterSeconds: skip.retry_after_seconds,
    })),
  }
}

export function ga4StreamCheckFromWire(body: Ga4StreamCheckOut): Ga4StreamCheck {
  return {
    propertyId: body.property_id,
    streamHosts: body.stream_hosts,
    websiteHosts: body.website_hosts,
    matches: body.matches,
    ownershipProvable: body.ownership_provable,
  }
}

function metricsFromWire(body: TrafficMetricsOut): Ga4TrafficMetrics {
  return {
    sessions: body.sessions,
    users: body.users,
    newUsers: body.new_users,
    engagedSessions: body.engaged_sessions,
    conversions: body.conversions,
    purchases: body.purchases,
    revenueMinor: body.revenue_minor,
  }
}

/** A row's `*_previous` fields as metrics; null when the report was not compared. */
function previousFromWire(row: TrafficRowOut): Ga4TrafficMetrics | null {
  if (row.sessions_previous === null || row.sessions_previous === undefined) return null
  return {
    sessions: row.sessions_previous,
    users: row.users_previous ?? 0,
    newUsers: row.new_users_previous ?? 0,
    engagedSessions: row.engaged_sessions_previous ?? 0,
    conversions: row.conversions_previous ?? 0,
    purchases: row.purchases_previous ?? 0,
    revenueMinor: row.revenue_minor_previous ?? null,
  }
}

function rowFromWire(row: TrafficRowOut): Ga4TrafficRow {
  return { ...metricsFromWire(row), key: row.key, label: row.label, previous: previousFromWire(row) }
}

export function trafficFromWire(body: TrafficOut): Ga4TrafficReport {
  return {
    source: body.source,
    start: body.start,
    end: body.end,
    groupBy: body.group_by,
    compared: body.compare !== null,
    previousStart: body.previous_start,
    previousEnd: body.previous_end,
    currency: body.currency,
    properties: body.properties.map((property) => ({
      ga4PropertyId: property.ga4_property_id,
      propertyId: property.property_id,
      displayName: property.display_name,
      currency: property.currency,
    })),
    rows: body.rows.map(rowFromWire),
    totals: metricsFromWire(body.totals),
    totalsPrevious: body.totals_previous ? metricsFromWire(body.totals_previous) : null,
    freshness: { lastImportAt: body.freshness.last_import_at, stale: body.freshness.stale },
  }
}

/** The query string both `/analytics/traffic` and its `.csv` take, so the download is the table on screen. */
export function trafficSearch(query: Ga4TrafficQuery): string {
  const params = new URLSearchParams({ from: query.from, to: query.to, group_by: query.groupBy })
  if (query.compare) params.set('compare', 'previous_period')
  if (query.propertyId) params.set('property_id', query.propertyId)
  return params.toString()
}

/**
 * What the page recognises in the API's refusal texts (`modules/ga4/service.py`
 * and `traffic.py`). `ApiError` carries the status and the message but drops
 * `details.reason`, so the message is the only signal; the patterns live here,
 * once, as the contract, and `__tests__/Ga4Errors.test.ts` pins them to the
 * server's literal strings.
 */
export const GA4_REFUSALS = {
  grantMissing: /as Viewer on property/i,
  propertyNotFound: /property \S+ not found/i,
  ownershipNotProven: /none of its web data streams/i,
  alreadyConnected: /already connected/i,
  rangeTooLong: /covers at most \d+ days/i,
  /** `Ga4MisconfiguredError` and `load_key`: "…not configured / misconfigured / not set up … this deployment". A bare 503 is not this. */
  notConfigured: /(?:not configured|misconfigured|not set up) (?:in|for) this deployment/i,
} as const
